import { useCallback } from 'react';
import type { DeploymentTopologyEnvironmentType } from '@wix/cli-astro-definitions';
import type { PlatformsType } from '@wix/cli-app-installer';
import { useProjectModel } from '../model.js';
import { createRedirectLink } from '../components/PlatformLink/RedirectLink.js';
import { writeDeploymentTopologyEnvironment } from '../files/topology.js';

interface UpdateDeploymentTopologyForSiteProps {
  armTag: string;
  siteUrl: string;
  environment: DeploymentTopologyEnvironmentType;
}

export function useUpdateDeploymentTopologyForSite(siteId: string) {
  const {
    model: { projectFolder },
  } = useProjectModel();

  return useCallback(
    async ({
      environment,
      siteUrl,
      armTag,
    }: UpdateDeploymentTopologyForSiteProps) => {
      await writeDeploymentTopologyEnvironment(projectFolder, environment, {
        id: armTag,
        site: { main: siteUrl },
        dashboard: {
          main: `https://manage.wix.com/dashboard/${siteId}?apps-override=${armTag}`,
        },
      });
    },
    [projectFolder, siteId]
  );
}

interface UpdateDeploymentTopologyForAppProps {
  armTag: string;
  environment: DeploymentTopologyEnvironmentType;
}

export function useUpdateDeploymentTopologyForApp(siteId?: string) {
  const {
    model: { projectFolder, config },
  } = useProjectModel();

  return useCallback(
    async ({ environment, armTag }: UpdateDeploymentTopologyForAppProps) => {
      const makeUrl = (platform: PlatformsType) =>
        createRedirectLink({
          platform,
          appId: config.appId,
          versionOverrideId: armTag,
          siteId,
        });

      await writeDeploymentTopologyEnvironment(projectFolder, environment, {
        id: armTag,
        site: { main: makeUrl('site') },
        editor: { main: makeUrl('editor') },
        dashboard: { main: makeUrl('dashboard') },
      });
    },
    [projectFolder, config.appId, siteId]
  );
}
